// The finish screen: what the student sees once there is nothing left to
// serve.
//
// Layout only. WHICH message appears is still decided by the caller, from the
// same session state as before - a session that has run out of questions while
// a topic still owes remediation gets different words from one that is finished
// - so this card takes the body as a prop and picks nothing itself.
//
// Used from two places, the player when it runs out of questions mid-flow and
// the overview when the student reopens a finished session, so it takes its
// numbers as props and owns no fetching.

import React from "react"
import { BMD_UI } from "../../constants/diagnostics/bmd.theme"
import { BMD_COPY } from "../../constants/diagnostics/bmd.constants"

export type BmdFinishCardProps = {
  sectionCount: number
  questionsAnswered: number
  /**
   * Total time spent, if the caller knows it.
   *
   * The mockup draws a third "24 min spent" stat. Nothing in the session state
   * carries a duration, and summing the per-attempt seconds would be a server
   * change rather than a UI one, so the stat is omitted rather than invented.
   * Pass a value here and it appears.
   */
  totalDurationSeconds?: number
  /** The state-dependent message. See the note at the top of the file. */
  body: string
  onBack?: () => void
  className?: string
}

/** Whole minutes, floored, with a 1 min floor so a fast test doesn't read 0. */
function minutesSpent(seconds: number): number {
  return Math.max(1, Math.floor(seconds / 60))
}

const Stat: React.FC<{ value: React.ReactNode; label: string }> = ({
  value,
  label,
}) => (
  <div className="text-center px-4 flex-grow-1">
    <div
      className="fw-bold"
      style={{ color: BMD_UI.TITLE, fontSize: 26, lineHeight: 1.2 }}
    >
      {value}
    </div>
    <div style={{ color: BMD_UI.BODY, fontSize: 13 }}>{label}</div>
  </div>
)

const Divider = () => (
  <div
    style={{ width: 1, alignSelf: "stretch", background: BMD_UI.TRACK }}
    aria-hidden="true"
  ></div>
)

export const BmdFinishCard: React.FC<BmdFinishCardProps> = ({
  sectionCount,
  questionsAnswered,
  totalDurationSeconds,
  body,
  onBack,
  className,
}) => {
  const showTime =
    totalDurationSeconds != null && Number(totalDurationSeconds) > 0

  return (
    <div
      className={`text-center ${className ?? ""}`}
      style={{ maxWidth: 640, margin: "0 auto" }}
    >
      <div
        className="d-inline-flex align-items-center justify-content-center mb-3"
        style={{
          width: 58,
          height: 58,
          borderRadius: "50%",
          backgroundColor: BMD_UI.LETTER_BG,
          color: BMD_UI.FILL,
        }}
        aria-hidden="true"
      >
        <i className="fas fa-check" style={{ fontSize: 22 }}></i>
      </div>

      <h2
        className="fw-bold mb-2"
        style={{ color: BMD_UI.TITLE, fontSize: 26 }}
      >
        {BMD_COPY.FINISH_TITLE}
      </h2>
      <p style={{ color: BMD_UI.BODY, fontSize: 15 }} className="mb-4">
        {body}
      </p>

      <div
        className="d-flex align-items-center justify-content-center py-3 mb-3"
        style={{
          border: BMD_UI.CARD_BORDER,
          borderRadius: 10,
          backgroundColor: "#fbfcfe",
        }}
      >
        <Stat
          value={sectionCount}
          label={
            sectionCount === 1
              ? BMD_COPY.SECTION_LABEL
              : BMD_COPY.SECTIONS_LABEL
          }
        />
        <Divider />
        <Stat
          value={questionsAnswered}
          label={BMD_COPY.FINISH_STAT_QUESTIONS}
        />
        {showTime ? (
          <>
            <Divider />
            <Stat
              value={`${minutesSpent(Number(totalDurationSeconds))} min`}
              label={BMD_COPY.FINISH_STAT_TIME}
            />
          </>
        ) : null}
      </div>

      <div
        className="d-flex align-items-start gap-2 mb-4 text-start"
        style={{ color: BMD_UI.BODY, fontSize: 13 }}
      >
        <i
          className="fas fa-info-circle mt-1"
          style={{ color: BMD_UI.MUTED }}
          aria-hidden="true"
        ></i>
        <span>{BMD_COPY.FINISH_NOTE}</span>
      </div>

      {/* Drawn only when there is somewhere to go. A child who presses a dead
          button has been told the app is broken. */}
      {onBack ? (
        <button
          type="button"
          className="btn w-100 fw-bold text-white"
          style={{
            backgroundColor: BMD_UI.ACCENT,
            borderColor: BMD_UI.ACCENT,
            borderRadius: 8,
            padding: "12px 20px",
          }}
          onClick={onBack}
        >
          {BMD_COPY.FINISH_CTA}
        </button>
      ) : null}
    </div>
  )
}

export default BmdFinishCard
